/**
 * PeriodontalIntelligenceCard — Phase B Section 4.
 *
 * Gingivitis + periodontitis environment dashboard with EFP stage / grade
 * anchor (if a clinician screening is on file) + associated systemic
 * pathways with PubMed citations.
 */
import React from "react";
import { Activity, AlertTriangle, ShieldCheck, Lightbulb, ArrowRight, Network } from "lucide-react";
import ScoreExplanation from "@/components/dental_intel/ScoreExplanation";

const BAND_PILL = {
  green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  red:    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
};

export default function PeriodontalIntelligenceCard({ data, isDark, lang = "bg", cardCls }) {
  const t = {
    bg: {
      title: "Пародонтално възпаление — образователна среда",
      score_label: "Перио среда",
      stage: "EFP Stage",
      grade: "Grade",
      favorable: "Защита",
      unfavorable: "Натоварване",
      pathways: "Системни пътища (асоциация — не причина)",
      opportunities: "Възможности",
    },
    en: {
      title: "Periodontal inflammation — educational environment",
      score_label: "Perio environment",
      stage: "EFP Stage",
      grade: "Grade",
      favorable: "Protection",
      unfavorable: "Burden",
      pathways: "Systemic pathways (association — not causation)",
      opportunities: "Opportunities",
    },
    es: {
      title: "Inflamación periodontal — entorno educativo",
      score_label: "Entorno perio",
      stage: "EFP Stage",
      grade: "Grade",
      favorable: "Protección",
      unfavorable: "Carga",
      pathways: "Vías sistémicas (asociación — no causalidad)",
      opportunities: "Oportunidades",
    },
  }[lang] || {};

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-periodontal-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Activity className="w-5 h-5 text-rose-500" />
          {t.title}
        </h3>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${BAND_PILL[data?.band || "yellow"]}`} data-testid="dhi-periodontal-score">
            {data?.score ?? "—"}/100
          </span>
          {(data?.efp_stage || data?.efp_grade) && (
            <span className="text-[10px] opacity-70" data-testid="dhi-perio-efp">
              {data?.efp_stage && `${t.stage}: ${data.efp_stage}`}
              {data?.efp_grade && ` · ${t.grade} ${data.efp_grade}`}
            </span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Block icon={ShieldCheck} title={t.favorable} items={data?.favorable} tone="emerald" />
        <Block icon={AlertTriangle} title={t.unfavorable} items={data?.unfavorable} tone="rose" />
      </div>

      {/* Pathway pills */}
      {data?.pathways && data.pathways.length > 0 && (
        <div className="mb-3" data-testid="dhi-perio-pathways">
          <div className="text-xs font-bold mb-1.5 flex items-center gap-1.5">
            <Network className="w-3.5 h-3.5 text-indigo-500" />
            {t.pathways}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.pathways.map((p, i) => (
              <span
                key={i}
                className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${
                  isDark ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-200" : "bg-indigo-50 border-indigo-200 text-indigo-700"
                }`}
              >
                {lang === "en" ? p.label_en : p.label_bg}
              </span>
            ))}
          </div>
        </div>
      )}

      {data?.opportunities && data.opportunities.length > 0 && (
        <div className={`p-3 rounded-lg ${isDark ? "bg-rose-500/10" : "bg-rose-50"}`}>
          <div className="text-xs font-bold mb-1.5 flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-rose-500" />
            {t.opportunities}
          </div>
          <ul className="space-y-1 text-xs">
            {data.opportunities.map((o, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <ArrowRight className="w-3 h-3 mt-0.5 text-rose-500 shrink-0" />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ScoreExplanation
        isDark={isDark}
        lang={lang}
        testid="dhi-perio-why"
        inputs={lang === "en"
          ? "HbA1c, HOMA-IR, hs-CRP, smoking, BMI, omega-3 index, Vit D, fiber. If EFP 2018 screening is on file, its score is used."
          : "HbA1c, HOMA-IR, hs-CRP, тютюнопушене, BMI, омега-3 индекс, Vit D, фибри. При проверка по EFP 2018 — нейната оценка се ползва."}
        formula={lang === "en"
          ? "Without screening: starts at 80, big penalties for HbA1c ≥ 6.5% (−18), smoking (−15), hs-CRP > 3 (−12), IR > 2.5 (−10); bonuses for omega-3 ≥ 8% (+6) and fiber ≥ 30g (+4). With EFP screening: 100 − (EFP × 10)."
          : "Без проверка: започва от 80; големи отстъпки при HbA1c ≥ 6.5% (−18), пушене (−15), hs-CRP > 3 (−12), ИР > 2.5 (−10); бонуси за омега-3 ≥ 8% (+6) и фибри ≥ 30g (+4). С проверка по EFP: 100 − (EFP × 10)."}
        factors={[
          ...((data?.favorable) || []).slice(0,3).map((f) => `✓ ${f}`),
          ...((data?.unfavorable) || []).slice(0,3).map((f) => `✗ ${f}`),
        ]}
      />
    </div>
  );
}

function Block({ icon: Icon, title, items, tone }) {
  const toneCls = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    rose:    "text-rose-600 dark:text-rose-400",
  }[tone];
  return (
    <div>
      <div className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${toneCls}`}>
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      <ul className="space-y-1 text-xs">
        {items && items.length ? items.map((f, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className={`mt-1 w-1 h-1 rounded-full shrink-0 ${tone === "emerald" ? "bg-emerald-500" : "bg-rose-500"}`} />
            <span className="opacity-80">{f}</span>
          </li>
        )) : (
          <li className="opacity-40 italic text-[11px]">—</li>
        )}
      </ul>
    </div>
  );
}
